import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  X, MapPin, Calendar, Clock, Users, User, 
  CreditCard, Share2, Heart, BadgeCheck, ChevronRight
} from 'lucide-react';
import FavoriteButton from './FavoriteButton';
import WhatsAppShareButton from './WhatsAppShareButton';
import VerifiedBadge from './VerifiedBadge';

const categoryColors = {
  yoga: '#9333ea',
  renforcement: '#ef4444',
  mobilite: '#22c55e',
  outdoor: '#3b82f6',
  cardio: '#ef4444',
  pilates: '#f97316',
  boxe: '#ef4444',
  danse: '#9333ea',
};

/**
 * Session Side Panel Component
 * Displays session details in a slide-out panel over the map
 */
export default function SessionSidePanel({ session, onClose, onBook }) {
  if (!session) return null;

  const spotsLeft = session.max_participants - session.current_participants;
  const isFull = spotsLeft <= 0;
  const isConfirmed = session.status === 'confirmed';

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[400px] bg-card border-l border-border z-[1001] shadow-2xl animate-slide-in-right overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <span 
            className="px-3 py-1 rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: categoryColors[session.category] || '#3b82f6' }}
          >
            {session.category}
          </span>
          <span className={`text-xs font-medium ${isConfirmed ? 'text-green-500' : 'text-yellow-500'}`}>
            {isConfirmed ? '✓ Confirmé' : 'En attente'}
          </span>
        </div>
        <button 
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          data-testid="close-panel-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Title & Actions */}
        <div>
          <div className="flex items-start justify-between gap-3 mb-2">
            <h2 className="text-xl font-bold">{session.title}</h2>
            <FavoriteButton sessionId={session.session_id} size="default" />
          </div>
          <p className="text-muted-foreground text-sm">{session.description}</p>
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Date</span>
            </div>
            <div className="font-medium text-sm">{formatDate(session.date)}</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Clock className="w-3.5 h-3.5" />
              <span>Horaire</span>
            </div>
            <div className="font-medium text-sm">{session.start_time} • {session.duration_minutes}min</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>Lieu</span>
            </div>
            <div className="font-medium text-sm line-clamp-1">{session.location}</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Users className="w-3.5 h-3.5" />
              <span>Places</span>
            </div>
            <div className={`font-medium text-sm ${isFull ? 'text-red-500' : 'text-green-500'}`}>
              {isFull ? 'Complet' : `${spotsLeft} disponible${spotsLeft > 1 ? 's' : ''}`}
            </div>
          </div>
        </div>

        {/* Coach */}
        {session.coach_name && (
          <div className="bg-muted/30 rounded-xl p-4">
            <div className="text-xs text-muted-foreground mb-2">Votre coach</div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                {session.coach?.picture ? (
                  <img src={session.coach.picture} alt={session.coach_name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-6 h-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-1.5 font-semibold">
                  {session.coach_name}
                  {session.coach_verified && <VerifiedBadge isVerified={true} size="small" />}
                </div>
                {session.coach?.profile?.experience_years > 0 && (
                  <div className="text-xs text-muted-foreground">
                    {session.coach.profile.experience_years} ans d'expérience
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Level */}
        <div>
          <div className="text-xs text-muted-foreground mb-2">Niveau requis</div>
          <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-medium
            ${session.level === 'debutant' ? 'bg-green-500/20 text-green-500' : ''}
            ${session.level === 'intermediaire' ? 'bg-yellow-500/20 text-yellow-500' : ''}
            ${session.level === 'avance' ? 'bg-red-500/20 text-red-500' : ''}
            ${session.level === 'tous' ? 'bg-blue-500/20 text-blue-500' : ''}
          `}>
            {session.level === 'debutant' && 'Débutant'}
            {session.level === 'intermediaire' && 'Intermédiaire'}
            {session.level === 'avance' && 'Avancé'}
            {session.level === 'tous' && 'Tous niveaux'}
          </span>
        </div>

        {/* Share */}
        <div className="pt-2">
          <WhatsAppShareButton session={session} variant="default" />
        </div>
      </div>

      {/* Footer - Price & Book */}
      <div className="p-4 border-t border-border bg-background/95 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-3xl font-bold text-primary">{session.price}€</div>
            <div className="text-xs text-muted-foreground">par personne</div>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            Min. {session.min_participants} participants
          </div>
        </div>
        
        {isFull ? (
          <Button disabled className="w-full h-12" data-testid="book-btn-disabled">
            Complet
          </Button>
        ) : (
          <div className="space-y-2">
            <Button 
              onClick={() => onBook(session.session_id)}
              className="btn-primary w-full h-12 text-base"
              data-testid="book-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Réserver maintenant
            </Button>
            <Link to={`/session/${session.session_id}`} className="block">
              <Button variant="outline" className="w-full" data-testid="view-details-btn">
                Voir tous les détails
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
